import {
    DocumentTypesEnum,
    DocumentTypeOptions,
    ReportStatusOptionsList,
} from '../../utilities/Constants';
import { ILookupModel, IMultiSelectOption } from '../../interfaces/ILookup';
import {
    AppliedFiltersDetailsBody,
    ISelectedFiltersDetails,
} from '../../components/shared/filters/AppliedFiltersDetailsBody';
import { AppliedFiltersSection } from '../../components/shared/filters/AppliedFiltersSection';
import { AppliedFiltersView } from '../../components/shared/filters/AppliedFiltersView';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import { FilterBadge } from '../../components/shared/filters/FilterBadge';
import { IDocumentSearchModel } from '../../interfaces/IDocument';
import { IReportTypeModel } from '../../interfaces/Report/IReport';
import { cloneDeep } from 'lodash';
import { useGetReportSubTypes } from '../../shared/react-query-hooks/useGetReportSubTypes';

const subTypeToMultiSelectOption = (subType: IReportTypeModel) => {
    return {
        id: subType.id,
        label: subType.displayName,
        value: subType.displayName,
    } as IMultiSelectOption;
};

interface IMyDocumentsAppliedFiltersProps {
    searchModel: IDocumentSearchModel;
    onApplyFilters: (filters: IDocumentSearchModel) => void;
    onToggleFilterPane: () => void;
}

export function MyDocumentsAppliedFilters(props: IMyDocumentsAppliedFiltersProps) {
    const reportSubTypesApi = useGetReportSubTypes();

    const details: ISelectedFiltersDetails[] = [];

    const handleRemoveType = (item: IMultiSelectOption) => {
        const searchModelCopy = cloneDeep(props.searchModel);
        searchModelCopy.types = searchModelCopy.types?.filter((t) => t.id !== item.id);

        const onlyOneTypeIsSelected = (searchModelCopy.types?.length ?? 0) === 1;
        const onlyReportTypeIsSelected =
            onlyOneTypeIsSelected && searchModelCopy.types?.[0].id === DocumentTypeEnum.Report;

        if (!onlyReportTypeIsSelected) {
            searchModelCopy.subTypes = [];
        }

        props.onApplyFilters(searchModelCopy);
    };

    const handleRemoveSubType = (item: ILookupModel) => {
        const searchModelCopy = cloneDeep(props.searchModel);
        searchModelCopy.subTypes = searchModelCopy.subTypes?.filter((st) => st.id !== item.id);
        props.onApplyFilters(searchModelCopy);
    };

    const handleRemoveStatus = (item: ILookupModel) => {
        const searchModelCopy = cloneDeep(props.searchModel);
        searchModelCopy.statuses = searchModelCopy.statuses?.filter(
            (statusId) => statusId !== item.id
        );
        props.onApplyFilters(searchModelCopy);
    };

    const handleRemoveReadUnread = () => {
        const searchModelCopy = cloneDeep(props.searchModel);
        searchModelCopy.isRead = undefined;
        props.onApplyFilters(searchModelCopy);
    };

    const handleRemoveStartDate = () => {
        const updatedSearchModel = {
            ...props.searchModel,
            startDate: undefined,
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const handleRemoveEndDate = () => {
        const updatedSearchModel = {
            ...props.searchModel,
            endDate: undefined,
        };
        props.onApplyFilters(updatedSearchModel);
    };

    const sortedSelectedTypes =
        DocumentTypeOptions.filter((opt) =>
            props.searchModel.types?.map((t) => t.id).includes(opt.id)
        ).sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? '')) ?? [];

    const sortedSelectedReportSubTypes =
        reportSubTypesApi.data
            ?.filter((st) => props.searchModel.subTypes?.map((st) => st.id).includes(st.id))
            ?.map(subTypeToMultiSelectOption)
            .sort((a, b) => (a?.label ?? '').localeCompare(b?.label ?? '')) ?? [];

    const sortedSelectedStatuses =
        ReportStatusOptionsList.filter((lu) => props.searchModel.statuses?.includes(lu.id)).sort(
            (a, b) => (a?.name ?? '').localeCompare(b?.name ?? '')
        ) ?? [];

    const sortedSelectedReadUnread = CheckmateSelectHelper.getReadUnreadOptions().filter((opt) => {
        if (props.searchModel.isRead === true) {
            return opt.id === 1;
        } else if (props.searchModel.isRead === false) {
            return opt.id === 0;
        }
    });

    return (
        <AppliedFiltersSection onToggleFilterPane={props.onToggleFilterPane}>
            <AppliedFiltersView selectedFilterDetails={details}>
                <>
                    {sortedSelectedTypes?.map((item) => (
                        <FilterBadge
                            key={item.id}
                            label={item.label}
                            onRemove={() => handleRemoveType(item)}
                        />
                    ))}
                    {sortedSelectedReportSubTypes?.map((item) => (
                        <FilterBadge
                            key={item.id}
                            label={item.label}
                            onRemove={() => handleRemoveSubType(item)}
                        />
                    ))}
                    {sortedSelectedStatuses?.map((item) => (
                        <FilterBadge
                            key={item.id}
                            label={item.name}
                            onRemove={() => handleRemoveStatus(item)}
                        />
                    ))}
                    {sortedSelectedReadUnread?.map((item) => (
                        <FilterBadge
                            key={item.id}
                            label={item.label}
                            onRemove={handleRemoveReadUnread}
                        />
                    ))}
                    {props.searchModel.startDate && (
                        <FilterBadge
                            key={'start-date-filter'}
                            label={props.searchModel.startDate}
                            onRemove={handleRemoveStartDate}
                        />
                    )}
                    {props.searchModel.endDate && (
                        <FilterBadge
                            key={'end-date-filter'}
                            label={props.searchModel.endDate}
                            onRemove={handleRemoveEndDate}
                        />
                    )}
                </>
            </AppliedFiltersView>
        </AppliedFiltersSection>
    );
}
